import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ComboBoxOption {
  value: string;
  label: string;
  hint?: string;
}

export interface ComboBoxProps {
  value: string | null;
  onChange: (value: string | null, option: ComboBoxOption | null) => void;
  fetchOptions: (query: string) => Promise<ComboBoxOption[]>;
  ariaLabel?: string;
  placeholder?: string;
  emptyText?: string;
  errorText?: string;
  disabled?: boolean;
  debounceMs?: number;
  initialLabel?: string;
  className?: string;
}

export function ComboBox({
  value,
  onChange,
  fetchOptions,
  ariaLabel,
  placeholder = 'Search...',
  emptyText = 'No matches',
  errorText = 'Could not load matches. Try again.',
  disabled,
  debounceMs = 300,
  initialLabel = '',
  className,
}: ComboBoxProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(initialLabel);
  const [options, setOptions] = useState<ComboBoxOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const found = options.find((o) => o.value === value);
    return found?.label ?? initialLabel;
  }, [value, options, initialLabel]);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Debounced fetch with stale-result protection
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      const id = ++requestIdRef.current;
      setLoading(true);
      setError(false);
      fetchOptions(query)
        .then((res) => {
          if (id !== requestIdRef.current) return;
          setOptions(res);
          setActiveIdx(0);
          setError(false);
        })
        .catch(() => {
          if (id !== requestIdRef.current) return;
          setOptions([]);
          setError(true);
        })
        .finally(() => {
          if (id !== requestIdRef.current) return;
          setLoading(false);
        });
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [query, open, fetchOptions, debounceMs]);

  const select = useCallback(
    (opt: ComboBoxOption) => {
      onChange(opt.value, opt);
      setQuery(opt.label);
      setOpen(false);
    },
    [onChange],
  );

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, options.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[activeIdx];
      if (opt) select(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={open ? query : selectedLabel || query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          className={cn(
            'block w-full rounded-md border bg-neutral-0 pl-12 pr-32 text-base text-neutral-900',
            'h-11 laptop:h-9',
            'border-neutral-200 placeholder:text-neutral-400',
            'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
            'disabled:bg-neutral-50 disabled:text-neutral-400',
          )}
        />
        <ChevronDown
          className="pointer-events-none absolute right-12 top-1/2 h-16 w-16 -translate-y-1/2 text-neutral-400"
          aria-hidden="true"
        />
      </div>
      {open && (
        <ul
          id={`${id}-list`}
          role="listbox"
          className="absolute z-[--z-dropdown] mt-4 max-h-64 w-full overflow-auto rounded-md border border-neutral-200 bg-neutral-0 shadow-md"
        >
          {loading ? (
            <li className="flex items-center gap-8 p-12 text-sm text-neutral-500">
              <Loader2 className="h-16 w-16 animate-spin" />
              Searching...
            </li>
          ) : error ? (
            <li className="p-12 text-sm text-error-600">{errorText}</li>
          ) : options.length === 0 ? (
            <li className="p-12 text-sm text-neutral-500">{emptyText}</li>
          ) : (
            options.map((opt, idx) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(opt);
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-8 p-12 text-sm',
                  idx === activeIdx ? 'bg-primary-500/10 text-neutral-900' : 'text-neutral-700',
                )}
              >
                <div className="flex flex-col">
                  <span>{opt.label}</span>
                  {opt.hint && <span className="text-xs text-neutral-500">{opt.hint}</span>}
                </div>
                {opt.value === value && <Check className="h-16 w-16 text-primary-500" />}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
